import React, { useState, useEffect, useCallback } from 'react';
import { reportsApi } from '../services/api';
import { TruckDetailedReport } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency } from '../utils/numberFormatter';

const TruckDetailedReportWidget: React.FC = () => {
  const { t } = useLanguage();
  const [reports, setReports] = useState<TruckDetailedReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      // For demonstration, using mock data when API is not available
      const mockData: TruckDetailedReport[] = [
        {
          licensePlate: '29A-12345',
          driverSalary: 15000000,
          loading: 5000000,
          tollFee: 2000000,
          rm2: 1500000,
          repair: 3000000,
          advancePayment: 10000000,
          remainingBalance: 8500000,
          tripCount: 8
        },
        {
          licensePlate: '30B-67890',
          driverSalary: 18000000,
          loading: 6200000,
          tollFee: 2500000,
          rm2: 1800000,
          repair: 2200000,
          advancePayment: 12000000,
          remainingBalance: 15700000,
          tripCount: 10
        },
        {
          licensePlate: '51C-11111',
          driverSalary: 12000000,
          loading: 4500000,
          tollFee: 1800000,
          rm2: 1200000,
          repair: 1500000,
          advancePayment: 8000000,
          remainingBalance: 7000000,
          tripCount: 6
        }
      ];

      try {
        const response = await reportsApi.getTruckDetailedReport(selectedYear, selectedMonth);
        setReports(response.data);
      } catch (error) {
        console.log('API not available, using mock data for demonstration');
        setReports(mockData);
      }
    } catch (error) {
      console.error('Error fetching truck detailed reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Generate year options (from 2020 to current year + 1)
  const years = Array.from({ length: new Date().getFullYear() - 2019 + 1 }, (_, i) => 2020 + i);
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">{t('truck.detailed.report')}</h5>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              style={{ width: 'auto' }}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              style={{ width: 'auto' }}
            >
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className="card-body">
        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('loading')}</span>
            </div>
          </div>
        ) : reports.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead className="table-dark">
                <tr>
                  <th className="text-center">{t('truck.number')}</th>
                  <th className="text-center">{t('driver.salary')}</th>
                  <th className="text-center">{t('loading')}</th>
                  <th className="text-center">{t('toll.fee')}</th>
                  <th className="text-center">{t('rm2')}</th>
                  <th className="text-center">{t('repair')}</th>
                  <th className="text-center">{t('advance.payment')}</th>
                  <th className="text-center">{t('remaining.balance')}</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report, index) => (
                  <tr key={report.licensePlate}>
                    <td className="text-center fw-bold bg-primary text-white">{report.licensePlate}</td>
                    <td className="text-end">{formatVietnamCurrency(report.driverSalary)}</td>
                    <td className="text-end">{formatVietnamCurrency(report.loading)}</td>
                    <td className="text-end">{formatVietnamCurrency(report.tollFee)}</td>
                    <td className="text-end">{formatVietnamCurrency(report.rm2)}</td>
                    <td className="text-end">{formatVietnamCurrency(report.repair)}</td>
                    <td className="text-end text-danger fw-bold">{formatVietnamCurrency(report.advancePayment)}</td>
                    <td className="text-end bg-warning fw-bold">{formatVietnamCurrency(report.remainingBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted text-center">
            {t('no.data.available')} {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
          </p>
        )}
      </div>
    </div>
  );
};

export default TruckDetailedReportWidget;